import { CSSProperties } from 'react';
import { createTheme } from '@mui/material/styles';

/**
 * Palette for the static pages (About Us, FAQs, Contact Us, Rental Agreement,
 * Testimonials). Values come from the approved design proposal (ks-pages.html)
 * and intentionally sit apart from the app's primary palette.
 */
interface StaticPagesPalette {
  navy: string;
  orange: string;
  blue: string;
  star: string;
  starMuted: string;
  text: {
    body: string;
    secondary: string;
    story: string;
    muted: string;
    light: string;
    onCta: string;
    onCtaLight: string;
  };
  border: {
    card: string;
    cardAlt: string;
    hero: string;
    callout: string;
    band: string;
  };
  bg: {
    callout: string;
    band: string;
  };
  gradient: {
    hero: string;
    cta: string;
  };
  shadow: {
    sm: string;
    md: string;
    lg: string;
  };
}

declare module '@mui/material/styles' {
  interface PaletteColor {
    darker?: string;
    900?: string;
    200?: string;
  }

  interface SimplePaletteColorOptions {
    darker?: string;
    900?: string;
    200?: string;
  }

  interface Palette {
    promo: {
      calloutBackground: string;
      calloutBorder: string;
      bulletFree: string;
      bulletName: string;
    };
    staticPages: StaticPagesPalette;
  }

  interface PaletteOptions {
    promo?: {
      calloutBackground: string;
      calloutBorder: string;
      bulletFree: string;
      bulletName: string;
    };
    staticPages?: StaticPagesPalette;
  }

  interface TypographyVariants {
    eyebrow: CSSProperties;
  }

  interface TypographyVariantsOptions {
    eyebrow?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    eyebrow: true;
  }
}

const baseTheme = createTheme({
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  palette: {
    primary: {
      main: '#406297',
      darker: '#103265',
      '900': '#315B99',
      '200': '#E4E9F1',
    },
    secondary: {
      main: '#FF8C25',
      darker: '#b36725',
      light: '#ffb472',
      contrastText: '#fff',
    },
    error: {
      main: '#E96B6B',
    },
    grey: {
      '400': '#B8B8B8',
      '600': '#666666',
      '900': '#2F2F2F',
    },
    warning: {
      main: '#ed6c02',
      light: '#F2994A',
    },
    promo: {
      calloutBackground: '#FFF3EA',
      calloutBorder: '#FBCE9B',
      bulletFree: '#FF0000',
      bulletName: '#3366FF',
    },
    staticPages: {
      navy: '#14396e',
      orange: '#f5991f',
      blue: '#3f6cb4',
      star: '#f5b301',
      starMuted: '#d9e0e9',
      text: {
        body: '#35404f',
        secondary: '#4a586b',
        story: '#3f4a5a',
        muted: '#667588',
        light: '#8595a8',
        onCta: '#d5e2f4',
        onCtaLight: '#eaf1fb',
      },
      border: {
        card: '#e4eaf1',
        cardAlt: '#e6edf5',
        hero: '#dbe6f2',
        callout: '#e4edf6',
        band: '#e7eef6',
      },
      bg: {
        callout: '#f7fafd',
        band: '#f5f8fc',
      },
      gradient: {
        hero: 'linear-gradient(180deg, #eef4fb 0%, #e4edf6 100%)',
        cta: 'linear-gradient(135deg, #3f6cb4 0%, #2f5698 100%)',
      },
      shadow: {
        sm: '0 2px 10px rgba(20,57,110,.04)',
        md: '0 4px 18px rgba(20,57,110,.05)',
        lg: '0 6px 24px rgba(20,57,110,.05)',
      },
    },
  },
});

export const DATE_CALENDAR_CLASS = 'ks-date-calendar';

// Type scale (second pass: responsive sizes need theme.breakpoints/pxToRem).
// body1 (16px) and body2 (14px) keep the MUI defaults; existing screens that
// use h1/h2 override fontSize/fontWeight locally, so redefining them is safe.
const themeLight = createTheme(baseTheme, {
  typography: {
    h1: {
      fontWeight: 700,
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
      fontSize: baseTheme.typography.pxToRem(32),
      [baseTheme.breakpoints.up('md')]: {
        fontSize: baseTheme.typography.pxToRem(44),
      },
    },
    h2: {
      fontWeight: 700,
      lineHeight: 1.2,
      fontSize: baseTheme.typography.pxToRem(22),
      [baseTheme.breakpoints.up('md')]: {
        fontSize: baseTheme.typography.pxToRem(26),
      },
    },
    h3: {
      fontWeight: 700,
      lineHeight: 1.3,
      fontSize: baseTheme.typography.pxToRem(20),
    },
    subtitle1: {
      lineHeight: 1.6,
      fontSize: baseTheme.typography.pxToRem(16),
      [baseTheme.breakpoints.up('md')]: {
        fontSize: baseTheme.typography.pxToRem(18),
      },
    },
    /** Small uppercase lead-in label above page/section titles */
    eyebrow: {
      fontWeight: 600,
      fontSize: baseTheme.typography.pxToRem(13),
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          eyebrow: 'div',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        [`.${DATE_CALENDAR_CLASS}`]: {
          '.rmdp-week-day': {
            fontSize: '12px',
            fontWeight: 400,
            color: 'rgba(0, 0, 0, 0.6)',
          },
          '.rmdp-day': {
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            span: { fontSize: '12px' },
            '.sd': { color: 'rgba(0, 0, 0, 0.87)' },
          },
          '.rmdp-day.rmdp-selected span:not(.highlight)': {
            backgroundColor: baseTheme.palette.primary.main,
            color: baseTheme.palette.common.white,
          },
        },
      },
    },
  },
});

export default themeLight;
